/**
 * 员工管理页面
 * - 管理员可以查看、新增、编辑、重置密码、启停员工
 * - 删除员工（中央账号表，删除后所有系统无法登录）
 * - 转为普通客户（保留用户名密码，收回后台权限，仅以客户身份登录）
 * - 所有角色可见但仅 admin 可操作
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Tag,
  Switch, Space, Popconfirm, message, Tooltip,
} from 'antd';
import {
  UserAddOutlined, EditOutlined, KeyOutlined,
  StopOutlined, CheckCircleOutlined, TeamOutlined,
  DeleteOutlined, UserSwitchOutlined,
} from '@ant-design/icons';
import { employeeApi } from '../services/api';

const roleMap = {
  admin:      { label: '管理员',   color: 'red',   desc: '全部功能权限' },
  technician: { label: '维修人员', color: 'blue',  desc: '维修工单、返修、安装' },
  shipper:    { label: '发货人员', color: 'green', desc: '销售下单、收款、发货' },
};

function EmployeeManage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  // 新增/编辑弹窗
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('新增员工');
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  // 重置密码弹窗
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdForm] = Form.useForm();
  const [resetTarget, setResetTarget] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeApi.list();
      const data = res.data?.list || res.list || [];
      setList(Array.isArray(data) ? data : []);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // ---------- 新增/编辑 ----------
  const openCreate = () => {
    setEditingId(null);
    setModalTitle('新增员工');
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setModalTitle(`编辑员工 - ${record.real_name}`);
    form.setFieldsValue({
      username: record.username,
      realName: record.real_name,
      role: record.role,
      phone: record.phone,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await employeeApi.update(editingId, {
          realName: values.realName,
          role: values.role,
          phone: values.phone,
        });
        message.success('员工信息已更新');
      } else {
        await employeeApi.create(values);
        message.success('员工创建成功');
      }
      setModalOpen(false);
      form.resetFields();
      loadList();
    } catch { /* validation error handled by form */ }
  };

  // ---------- 重置密码 ----------
  const openResetPwd = (record) => {
    setResetTarget(record);
    pwdForm.resetFields();
    setPwdModalOpen(true);
  };

  const handleResetPwd = async () => {
    try {
      const values = await pwdForm.validateFields();
      await employeeApi.resetPassword(resetTarget.id, values.newPassword);
      message.success(`${resetTarget.real_name} 的密码已重置`);
      setPwdModalOpen(false);
    } catch { }
  };

  // ---------- 切换启停 ----------
  const toggleStatus = async (record) => {
    const newStatus = record.status === 1 ? 0 : 1;
    await employeeApi.updateStatus(record.id, newStatus);
    message.success(newStatus === 1 ? '已启用' : '已停用');
    loadList();
  };

  // ---------- 删除员工 ----------
  const handleDelete = async (record) => {
    try {
      await employeeApi.remove(record.id);
      message.success(`员工「${record.real_name}」已删除`);
      loadList();
    } catch { /* 错误提示由拦截器统一处理 */ }
  };

  // ---------- 转为普通客户 ----------
  const handleConvert = async (record) => {
    try {
      await employeeApi.convertToCustomer(record.id);
      message.success(`「${record.real_name}」已转为普通客户（用户名密码不变）`);
      loadList();
    } catch { /* 错误提示由拦截器统一处理 */ }
  };

  // ---------- 表格列 ----------
  const columns = [
    {
      title: '用户名', dataIndex: 'username', key: 'username', width: 120,
      render: (v, r) => (
        <Space>
          <span style={{ fontWeight: 500 }}>{v}</span>
          {r.status === 0 && <Tag color="#ff4d4f" style={{ fontSize: 10 }}>停用</Tag>}
        </Space>
      ),
    },
    { title: '姓名', dataIndex: 'real_name', key: 'real_name', width: 100 },
    {
      title: '角色', dataIndex: 'role', key: 'role', width: 110,
      render: (v) => <Tag color={roleMap[v]?.color}>{roleMap[v]?.label || v}</Tag>,
    },
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 130, render: (v) => v || '-' },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: (v) => (
        <Switch
          checked={v === 1}
          size="small"
          onChange={() => { }}
          style={{ cursor: 'not-allowed', opacity: 0.7 }}
        />
      ),
    },
    {
      title: '最后登录', dataIndex: 'last_login', key: 'last_login', width: 160,
      render: (v) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160,
      render: (v) => new Date(v).toLocaleDateString('zh-CN'),
    },
    {
      title: '操作', key: 'action', width: 260, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="重置密码">
            <Button type="link" size="small" icon={<KeyOutlined />} onClick={() => openResetPwd(record)} />
          </Tooltip>
          <Popconfirm
            title={record.status === 1 ? `确定停用 ${record.real_name}？` : `确定启用 ${record.real_name}？`}
            onConfirm={() => toggleStatus(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title={record.status === 1 ? '停用' : '启用'}>
              <Button
                type="link" size="small" danger={record.status === 1}
                icon={record.status === 1 ? <StopOutlined /> : <CheckCircleOutlined />}
              />
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title={`确定将 ${record.real_name} 转为普通客户？`}
            description="收回后台权限，原用户名密码改以客户身份登录，可在小程序端报修"
            onConfirm={() => handleConvert(record)}
            okText="转换"
            cancelText="取消"
          >
            <Tooltip title="转为普通客户">
              <Button type="link" size="small" icon={<UserSwitchOutlined />} />
            </Tooltip>
          </Popconfirm>
          <Popconfirm
            title={`确定删除员工 ${record.real_name}？`}
            description="删除后不可恢复，该账号在主站/维修/充电桩所有系统均无法登录"
            onConfirm={() => handleDelete(record)}
            okText="删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="删除">
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={<Space><TeamOutlined />员工管理</Space>}
      extra={<Button type="primary" icon={<UserAddOutlined />} onClick={openCreate}>新增员工</Button>}
    >
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        pagination={false}
        size="middle"
      />

      {/* ========== 新增/编辑弹窗 ========== */}
      <Modal
        title={modalTitle}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="username" label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '至少3个字符' },
            ]}
          >
            <Input disabled={!!editingId} placeholder="登录账号" />
          </Form.Item>

          {!editingId && (
            <Form.Item
              name="password" label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '至少6位' },
              ]}
            >
              <Input.Password placeholder="至少6位密码" />
            </Form.Item>
          )}

          <Form.Item
            name="realName" label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="真实姓名" />
          </Form.Item>

          <Form.Item
            name="role" label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="选择角色">
              {Object.entries(roleMap).map(([k, v]) => (
                <Select.Option key={k} value={k}>{v.label} - {v.desc || ''}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="phone" label="手机号">
            <Input placeholder="手机号（选填）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ========== 重置密码弹窗 ========== */}
      <Modal
        title={`重置密码 - ${resetTarget?.real_name || ''}`}
        open={pwdModalOpen}
        onOk={handleResetPwd}
        onCancel={() => { setPwdModalOpen(false); pwdForm.resetFields(); }}
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="newPassword" label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '至少6位' },
            ]}
          >
            <Input.Password placeholder="输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

export default EmployeeManage;
